/* eslint-disable react/prop-types */
import React from 'react'
import { Col, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { ActionButton } from '@augmentt-dev/ui-components'
import {
  formatMfaConfigurationColumn,
  formatMfaStatusType,
  getAuthenticationTypeFormatter,
} from '../../../../../util/table.util'
import useIsAuthorized from '../../../ThreatAlerts/useIsAuthorized'
import useCustomerIntegration from '../../../../../hooks/useCustomerIntegration'

import StatusBadge from '../../../../../components/StatusBadge'
import DetailRow from '../../../../../components/DetailRow'
import Pill from '../../../../../components/Pill'
import { CONTENT_MARGINS } from '../utils'
import EmployeeActionsSection from '../EmployeeActionsSection'
import PopoverMenu from '../../../../../components/PopoverMenu'

/** Known MFA status values from API */
const MFA_STATUS_KNOWN = ['PROTECTED', 'SIGN_IN_BLOCKED', 'BREAK_GLASS', 'NOT_PROTECTED', 'UNKNOWN', 'LOCKED_OUT']

/** Label for MFA status; uses INTEGRATION.* i18n when known, otherwise "Unknown". */
const getMfaStatusLabel = (status, intl) => {
  if (!status) return intl.formatMessage({ id: 'INTEGRATION.UNKNOWN' })
  if (MFA_STATUS_KNOWN.includes(status)) return intl.formatMessage({ id: `INTEGRATION.${status}` })
  return intl.formatMessage({ id: 'INTEGRATION.UNKNOWN' })
}

/** Registration from mfaStatus.statusList only */
const getMfaRegistrationDisplay = (employee, intl) => {
  const list = employee?.mfaStatus?.statusList
  if (list?.length) {
    return list
      .map(
        (i) =>
          `${formatMfaStatusType(i.type, intl)}${i.status ? ` - ${intl.formatMessage({ id: `GENERAL.${i.status}` })}` : ''}`,
      )
      .join(', ')
  }
  return 'None'
}

/** Authentication methods: actual registered methods (e.g. Phone, Authenticator), same pattern as Reports → MFA Report. */
const getAuthMethodsDisplay = (employee, intl) => {
  const methods = employee?.authenticationType
  if (methods?.length) {
    const format = getAuthenticationTypeFormatter(intl)
    return methods.map(format).join(', ')
  }
  return 'None'
}

function Overview(props) {
  const {
    employee,
    selectedCustomer,
    onEdit,
    intl,
    overrideEngageLicensing = false,
    canPerformActions = true,
    setAction,
    setModal,
    openIntegrationModal,
  } = props

  // Get integration app name for authorization
  const customerIntegration = useCustomerIntegration({
    selectedCustomerId: selectedCustomer?.id,
  })
  const integrationAppName = customerIntegration?.IntegrationApplication?.application_name
  const isIntegrationAuthorized = useIsAuthorized({
    integrationAppName,
    selectedCustomerId: selectedCustomer?.id,
  })
  const isAuthorized = canPerformActions && isIntegrationAuthorized

  return (
    <>
      {/* Action Buttons Row */}
      <div
        style={{
          paddingLeft: '0.75rem',
          paddingRight: '1rem',
          paddingBottom: '0.75rem',
          borderBottom: '1px solid var(--aug-ui-divider-subtle)',
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: '0.375rem' }}>
          <ActionButton
            icon="fas fa-pen-to-square"
            label={<FormattedMessage id="GENERAL.EDIT" />}
            variant="primary"
            disabled={!canPerformActions}
            onClick={() => onEdit()}
          />
          {employee?.employeeIdentifier && (
            <EmployeeActionsSection
              isAuthorized={isAuthorized}
              openIntegrationModal={openIntegrationModal}
              selectedEmployees={[employee]}
              overrideEngageLicensing={overrideEngageLicensing}
            />
          )}
          {employee?.employeeIdentifier && canPerformActions && (
            <PopoverMenu
              config={[
                {
                  title: 'ENGAGE.SECURITY_ACTIONS',
                  items: [
                    { id: 'revoke-sessions', label: 'ENGAGE.SIGN_OUT_ALL_APPS_NO_HYPHEN' },
                    { id: 're-register-mfa' },
                    { id: 'temporary-access-pass' },
                  ],
                },
                {
                  title: 'ENGAGE.MANAGEMENT_ACTIONS',
                  items: [
                    {
                      id: 'groups',
                      label: 'ENGAGE.GROUPS',
                      onClick: () => {}, // No action, just expand sub-menu
                      subMenu: {
                        title: 'ENGAGE.EDIT_GROUPS',
                        items: [
                          { id: 'manage-groups', label: 'ENGAGE.M365' },
                          { id: 'manage-security-groups', label: 'ENGAGE.SECURITY' },
                          { id: 'manage-distribution-mailboxes', label: 'ENGAGE.DISTRIBUTION_MAIL_ENABLED_SECURITY' },
                        ],
                      },
                    },
                    { id: 'manage-licenses' },
                    { id: 'out-of-office' },
                    { id: 'global-address-list' },
                    { id: 'manage-email-forwarding' },
                    { id: 'manage-shared-mailboxes', label: 'ENGAGE.SHARED_MAILBOX_ACCESS' },
                    { id: 'offboard-user' },
                  ],
                },
              ]}
              employee={employee}
              context={{
                isAuthorized,
                openIntegrationModal,
                selectedIntegration: customerIntegration,
                integrationType: integrationAppName === 'Office365' ? 'Office365' : 'GWS',
                setAction,
                setModal,
                actionConditions: {
                  'global-address-list': (emp) => ({
                    isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
                  }),
                  'manage-email-forwarding': (emp) => ({
                    isAvailable: emp?.isUserMailBoxAvailable || emp?.isUserSharedMailbox,
                  }),
                },
              }}
              toggleProps={{
                icon: 'fas fa-ellipsis',
                iconStyle: { color: 'var(--aug-act-icon)' },
                variant: 'light',
                style: { height: '28px', padding: '0 9px' },
              }}
            />
          )}
        </div>
      </div>

      <div className="section px-4 py-4">
        {/* Account Configuration Section */}
        <Row className="mb-4">
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Account Configuration
            </h6>
            <DetailRow
              label="User type"
              value={
                <Pill
                  variant="secondary"
                  size="medium"
                  withMargins={false}
                  style={{ marginLeft: CONTENT_MARGINS.CUSTOM_PILL }}
                >
                  {employee.userType || 'Member'}
                </Pill>
              }
              size="sm"
            />

            <DetailRow
              label="Sign-in status"
              value={
                employee.isUserSigninEnabled === false || employee.enabled === false ? (
                  <StatusBadge variant="danger" marginLeft={CONTENT_MARGINS.BADGE_PILL}>
                    Blocked sign-in
                  </StatusBadge>
                ) : (
                  <StatusBadge variant="success" marginLeft={CONTENT_MARGINS.BADGE_PILL}>
                    Allowed
                  </StatusBadge>
                )
              }
              size="sm"
            />

            <DetailRow
              label="Roles"
              value={
                employee.role && employee.role.length > 0
                  ? employee.role.map((role, index) => (
                      <Pill
                        key={role}
                        variant="secondary"
                        size="medium"
                        withMargins={false}
                        style={{
                          marginLeft: index === 0 ? CONTENT_MARGINS.CUSTOM_PILL_FIRST : '0',
                          marginRight: '4px',
                          marginBottom: '4px',
                        }}
                      >
                        {role}
                      </Pill>
                    ))
                  : 'User'
              }
              size="sm"
            />

            <DetailRow
              label="Groups"
              value={(() => {
                // Flatten groupsByType into a single array
                const allGroups = employee.groupsByType ? Object.values(employee.groupsByType).flat() : []
                return allGroups.length > 0 ? allGroups.map((group) => group.name).join(', ') : ''
              })()}
              size="sm"
            />

            <DetailRow
              label="Licenses"
              value={
                employee.m365Licenses && employee.m365Licenses.length > 0
                  ? employee.m365Licenses
                      .filter((license) => license != null)
                      .map((license) => license?.product_display_name || license?.licence_id)
                      .filter(Boolean)
                      .join(', ')
                  : ''
              }
              size="sm"
            />

            <DetailRow
              label="Mailbox rules"
              value={
                employee?.forwardingSmtpAddress || employee?.forwarding_smtp_address ? (
                  <StatusBadge variant="warning" marginLeft={CONTENT_MARGINS.BADGE_PILL_WARNING}>
                    Mail forwarding enabled
                  </StatusBadge>
                ) : (
                  ''
                )
              }
              size="sm"
            />

            <DetailRow
              label="Created"
              value={
                employee.createdDateTime
                  ? intl.formatDate(employee.createdDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>

        {/* Multifactor Authentication Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Multifactor Authentication
            </h6>
            <DetailRow
              label="Status"
              value={
                <span className="d-inline-block" style={{ overflow: 'hidden' }}>
                  <div
                    className={
                      employee?.mfaStatus?.status && MFA_STATUS_KNOWN.includes(employee.mfaStatus.status)
                        ? `duo${employee.mfaStatus.status}`
                        : 'duoUNKNOWN'
                    }
                  />
                  {getMfaStatusLabel(employee?.mfaStatus?.status ?? null, intl)}
                </span>
              }
              size="sm"
            />

            <DetailRow label="Registration" value={getMfaRegistrationDisplay(employee, intl)} size="sm" />

            <DetailRow label="Authentication methods" value={getAuthMethodsDisplay(employee, intl)} size="sm" />

            <DetailRow
              label="MFA Configuration"
              value={
                employee?.mfaConfigurations != null ? formatMfaConfigurationColumn(null, employee, null, intl) : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>

        {/* Recent Activity Section */}
        <Row
          className="mb-4"
          style={{ borderTop: '1px solid var(--aug-ui-divider-subtle)', paddingTop: '1.5rem', marginTop: '1.5rem' }}
        >
          <Col sm="12">
            <h6 className="text-bold mb-3" style={{ fontSize: '1rem', color: 'var(--bs-body-color)' }}>
              Recent Activity
            </h6>

            <DetailRow
              label="Last password change"
              value={
                employee.lastPasswordChangeDateTime
                  ? intl.formatDate(employee.lastPasswordChangeDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />

            <DetailRow
              label="Sign-in sessions valid from date"
              value={
                employee.refreshTokensValidFromDateTime
                  ? intl.formatDate(employee.refreshTokensValidFromDateTime, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })
                  : 'None'
              }
              size="sm"
            />
          </Col>
        </Row>
      </div>
    </>
  )
}

export default Overview
